<template lang="pug">
.list-pedidos-tienda-container
  //- Header Desktop
  .header-pedidos.d-none.d-md-flex
    .title-section
      b-icon.back-arrow(icon="arrow-left-square-fill" @click="$router.go(-1)")
      h4.title Pedidos de la Tienda
    .actions-section
      b-input-group.search-input
        b-input-group-prepend
          span.input-group-text
            b-icon(icon="search")
        b-form-input(
          v-model="paramsSearch"
          placeholder="Buscar por OT / Nombre Cliente / N° serie / Modelo"
          @keyup.enter="buscar"
        )

  //- Breadcrumbs
  .breadcrumbs-pedidos
    span.text-muted Inicio / Pedidos /
    span.text-dark Pedidos de la Tienda

  //- Tabla
  .table-wrapper
    b-table(
      :items="items"
      :fields="fields"
      hover
      responsive
      show-empty
      :busy="loadingTable"
    )
      template(#table-busy)
        .text-center.p-3
          b-spinner.mr-2(small)
          span Cargando...

      template(#empty)
        .empty-state-content
          img.empty-image(src="@/assets/undraw_no_data_re_kwbl.png" alt="Sin pedidos")
          h5.empty-title {{ emptyTitle }}
          p.empty-text {{ emptyText }}

      template(#cell(id)="data")
        span.blue-text {{ data.item.id }}

      template(#cell(tipo_envio)="data")
        span {{ tipoEnvioLabel(data.item.tipo_envio) }}

      template(#cell(origen)="data")
        b-badge.pedido-badge(
          :style="badgeStyle(origenBadge(data.item.origen))"
        ) {{ origenBadge(data.item.origen).text }}

      template(#cell(estado_pago)="data")
        b-badge.pedido-badge(
          :style="badgeStyle(estadoPagoBadge(data.item.estado_pago))"
        )
          b-icon.mr-1(:icon="estadoPagoBadge(data.item.estado_pago).icon")
          | {{ estadoPagoBadge(data.item.estado_pago).text }}

      template(#cell(total)="data")
        strong {{ data.item.total | formatCurrency }}

      template(#cell(acciones)="data")
        b-dropdown.btn-acciones(variant="warning" no-caret right)
          template(#button-content)
            img.mr-2(src="@/assets/icons/actions.svg" alt="Acciones" style="width: 20px; height: 20px")
            span Acciones
          b-dropdown-item(@click="verDetalle(data.item.id)") Ver Detalle
          b-dropdown-item(
            :disabled="data.item.estado_pago !== ESTADO_PAGO_ACEPTADO"
            @click="descargarDocumento(data.item.id)"
          ) Descargar boleta/factura

    //- Paginación
    .pagination-wrapper(v-if="totalRows > pageSize")
      b-pagination(
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="pageSize"
        align="right"
        prev-text="Ant."
        next-text="Sig."
        @change="handlePageChange"
      )
</template>

<script>
import { mapActions } from 'vuex'

const ESTADO_PAGO_ACEPTADO = 2

export default {
  name: 'PedidosTiendaListView',
  data() {
    return {
      ESTADO_PAGO_ACEPTADO,
      paramsSearch: '',
      loadingTable: false,
      currentPage: 1,
      pageSize: 10,
      items: [],
      totalRows: 0,
      searchApplied: false,
      fields: [
        { key: 'id', label: 'N° Pedido' },
        { key: 'rut', label: 'RUT' },
        { key: 'nombre', label: 'Nombre' },
        { key: 'apellido', label: 'Apellido' },
        { key: 'tipo_envio', label: 'Tipo de Envio' },
        { key: 'origen', label: 'Origen Pedido' },
        { key: 'estado_pago', label: 'Estado Pago' },
        { key: 'total', label: 'Monto Total' },
        { key: 'acciones', label: 'Acciones', class: 'text-center' },
      ],
    }
  },
  computed: {
    emptyTitle() {
      return this.searchApplied
        ? 'No se encontraron pedidos'
        : 'Aún no hay pedidos registrados'
    },
    emptyText() {
      return this.searchApplied
        ? 'No hay pedidos que coincidan con tu búsqueda. Intenta con otro término.'
        : 'Actualmente no existen ventas provenientes de la Tienda Online, ' +
            'Center ni Cotizaciones OVE. Cuando un cliente realice una compra ' +
            'o generes un pedido manual, aparecerá en esta lista.'
    },
  },
  methods: {
    ...mapActions('PedidosTiendaStore', ['fetchPedidos', 'downloadDocumento']),

    async fetchData() {
      this.loadingTable = true
      try {
        const res = await this.fetchPedidos({
          page: this.currentPage,
          q: this.paramsSearch,
        })
        this.items = res.results
        this.totalRows = res.count
      } catch (err) {
        this.$bvToast.toast('Error al cargar los pedidos', {
          variant: 'danger',
        })
        this.items = []
        this.totalRows = 0
      } finally {
        this.loadingTable = false
      }
    },

    buscar() {
      this.currentPage = 1
      this.searchApplied = this.paramsSearch.trim().length > 0
      this.fetchData()
    },

    handlePageChange(page) {
      this.currentPage = page
      this.fetchData()
    },

    verDetalle(id) {
      this.$router.push(`/pedidos-tienda/${id}`)
    },

    async descargarDocumento(id) {
      try {
        await this.downloadDocumento({ id })
      } catch (err) {
        this.$bvToast.toast('Error al descargar el documento', {
          variant: 'danger',
        })
      }
    },

    origenBadge(origen) {
      const map = {
        tienda: { text: 'Tienda Online', bg: '#f3e8ff', color: '#6b21a8' },
        center: { text: 'Center', bg: '#e1effe', color: '#1e429f' },
        cotizacion: { text: 'Cotización OVE', bg: '#d7d7d7', color: '#444444' },
      }
      return map[origen] || { text: 'N/A', bg: '#e9ecef', color: '#525252' }
    },

    estadoPagoBadge(estado) {
      const map = {
        1: {
          text: 'Pendiente',
          bg: '#fef3c7',
          color: '#92400e',
          icon: 'clock-fill',
        },
        2: {
          text: 'Aceptado',
          bg: '#e6f4ea',
          color: '#137333',
          icon: 'check-circle-fill',
        },
        3: {
          text: 'Rechazado',
          bg: '#fde8e8',
          color: '#ff3838',
          icon: 'x-circle-fill',
        },
      }
      return (
        map[estado] || { text: '—', bg: '#e9ecef', color: '#525252', icon: '' }
      )
    },

    tipoEnvioLabel(tipo) {
      return tipo === 2 ? 'Oficina' : 'Domicilio'
    },

    badgeStyle(badge) {
      return {
        backgroundColor: badge.bg,
        color: badge.color,
      }
    },
  },
  mounted() {
    this.fetchData()
  },
}
</script>

<style lang="scss" scoped>
@import '@/style/colors.scss';

.list-pedidos-tienda-container {
  padding: 20px;
  background-color: #f5f6fa;
  min-height: 100vh;
}

.header-pedidos {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 4rem;

  .title-section {
    display: flex;
    align-items: center;
    gap: 15px;

    .back-arrow {
      color: $samtek-yellow;
      font-size: 32px;
      cursor: pointer;
    }

    .title {
      margin: 0;
      font-weight: bold;
      color: $samtek-black;
    }
  }

  .actions-section {
    display: flex;
    align-items: center;
    gap: 10px;

    .search-input {
      width: 415px;
    }
  }
}

.breadcrumbs-pedidos {
  margin-bottom: 25px;
  font-size: 14px;
}

.table-wrapper {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  ::v-deep .b-table {
    thead th {
      background-color: #203859;
      color: #ffffff;
      border: none;
      font-weight: bold;
    }

    tbody td {
      vertical-align: middle;
      border-bottom: 1px solid #f5f6fa;
      height: 60px;
    }
  }
}

.blue-text {
  color: #1d4ed8;
  font-weight: 700;
}

.pedido-badge {
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
}

.btn-acciones {
  ::v-deep .btn {
    background-color: $samtek-yellow;
    border: none;
    color: $samtek-black;
    border-radius: 8px;
    font-weight: 500;
    font-size: 14px;
    display: flex;
    align-items: center;

    &:hover {
      background-color: darken($samtek-yellow, 5%);
    }
  }
}

.pagination-wrapper {
  margin-top: 20px;

  ::v-deep .page-item {
    &.active .page-link {
      background-color: $samtek-yellow;
      border-color: $samtek-yellow;
      color: $samtek-black;
    }

    .page-link {
      color: $samtek-black;
      border-radius: 4px;
      margin: 0 2px;
    }
  }
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  max-width: 910px;
  margin: 40px auto;
}

.empty-image {
  width: 485px;
  height: 302px;
  object-fit: contain;
}

.empty-title {
  font-family: 'Noto Sans', sans-serif;
  font-weight: 700;
  font-size: 28px;
  color: $samtek-black;
  margin: 0;
}

.empty-text {
  font-family: 'Noto Sans', sans-serif;
  font-size: 16px;
  color: $samtek-grey;
  margin: 0;
}
</style>
